import {
    Box,
    Button,
    Card,
    Checkbox,
    Chip, CircularProgress,
    Divider,
    FormControl, FormHelperText,
    FormLabel, Grid,
    Input, Radio, radioClasses,
    RadioGroup,
    Stack,
    Typography, useTheme
} from "@mui/joy";
import React, {useEffect, useState} from "react";
import {
    AppleIcon, ArrowLeft, BookOpen,
    CheckCircle2,
    ClipboardList, Eye, EyeOff, LanguagesIcon,
    LockIcon,
    LucideApple,
    LucideMail,
    UserIcon
} from "lucide-react";
import {Link} from "react-router-dom";
import {EXAM_LANGUAGES, EXAM_TYPE_MAPPER, ExamScoreName, ExamType, LANGUAGE_TIERS, LanguageTier} from "../models/exam";
import {motion, AnimatePresence} from "motion/react"
import {User, UserFormData} from "../models/user";
import {useAuth} from "../hooks/useAuth";
import {useUITheme} from "../hooks/useUITheme";
import {useTranslation} from "react-i18next";


const Register = () => {
    const auth = useAuth()
    const uiTheme = useUITheme()
    const [currentStep, setCurrentStep] = useState(0)
    const [userFormData, setUserFormData] = useState<UserFormData>({
        preferredExam: "ielts",
    })
    const [formValid, setFormValid] = useState(false)
    const {t} = useTranslation()

    const handleNavigate = () => {
        if (currentStep === 1)
            handleSubmit()
        else
            setCurrentStep(currentStep === 1 ? 0 : 1)
    }

    const handlePrevious = () => {
        setCurrentStep(currentStep === 1 ? 0 : 1)
    }

    const handleSubmit = () => {
        auth.register(userFormData)
    }

    const fieldValidation: {
        [key in keyof UserFormData]: {
            required: boolean,
            message?: string,
            check?: boolean
        }
    } = {
        name: {
            check: new RegExp(/^[A-Za-zÀ-ÖØ-öø-ÿ' -]{2,}$/).test(userFormData?.name),
            message: t("invalidNameFieldMessage"),
            required: true
        },
        email: {
            required: true,
            message: t("invalidEmailFieldMessage"),
            check: new RegExp(/^[^\s@]+@[^\s@]+\.[^\s@]+$/).test(userFormData?.email)
        },
        password: {
            check: userFormData?.password?.length >= 6,
            message: t("invalidPasswordFieldMessage"),
            required: true
        },
        confirmPassword: {
            check: userFormData?.confirmPassword === userFormData?.password,
            message: t("invalidConfirmPasswordMessage"),
            required: true
        },
        agreedToTermsAndCondition: {
            check: !!userFormData.agreedToTermsAndCondition,
            message: t("termsAndConditionsNotAgreedMessage"),
            required: true
        }
    }


    const validateForm = () => {
        setFormValid(Object.keys(fieldValidation)?.every(value =>
            fieldValidation[value].required ? !!userFormData[value] && fieldValidation[value].check : !userFormData[value] || fieldValidation[value].check))
    }

    const handleFormDataChange = (key: keyof UserFormData, value: any) => {
        setUserFormData({...userFormData, [key]: value})
    }

    useEffect(() => {
        validateForm()
    }, [userFormData])

    return <Stack paddingX={{xs: 2, sm: 7}} justifyContent={"center"} gap={2} alignItems={"center"} paddingY={3}
                  sx={{backgroundColor: "background.surface"}}>


        <Card variant={"outlined"}
              sx={{
                  paddingX: {xs: 2.5, sm: 5},
                  alignItems: "center",
                  gap: 5,
                  width: {xs: "100%", sm: "auto"},
                  minWidth: {sm: "35rem"},
                  boxSizing: "border-box",
                  paddingY: 4
              }}>

            <Stack alignItems={"center"}>
                <Box component={"img"} width={"10rem"}
                     height={"auto"} src={uiTheme.fullLogo}
                     alt={'LinguaScore Logo'}
                />
                <Typography level={"h3"} alignSelf={"center"}>
                    {t("registerFormTitle")}
                </Typography>

                <Typography level={"body-md"} textAlign={"center"}>
                    {t("registerFormDescription")}
                </Typography>
            </Stack>

            {currentStep === 0 && <RegisterPersonalInfoStep userFormData={userFormData}
                                                            onFormDataChange={handleFormDataChange}
                                                            fieldValidation={fieldValidation}
                                                            currentStep={currentStep}/>}

            {currentStep === 1 && <TestInfoStep onFormDataChange={handleFormDataChange} userFormData={userFormData}/>}

            <Stack direction={"row"} gap={2} width={"100%"}>
                <Button fullWidth onClick={handlePrevious} variant={"outlined"} disabled={currentStep === 0}>
                    {t("previousRegistrationStepActionLabel")}
                </Button>
                <Button disabled={!formValid} loading={auth.loading}
                        loadingIndicator={<CircularProgress color={"neutral"} variant={"plain"} determinate={false}/>}
                        size={"lg"} fullWidth onClick={handleNavigate}>
                    {currentStep === 1 ? t("registerActionButtonLabel") : t("nextRegistrationStepActionLabel")}
                </Button>

            </Stack>

            {currentStep === 0 &&
                <Stack gap={1.5} width={"100%"}>
                    <Button color={"danger"} variant={"outlined"}
                            sx={{borderRadius: 30}}
                            onClick={() => auth.authenticateWithProvider("google")}
                            startDecorator={<Box component={'img'}
                                                 src={require("../assets/google_logo.png")}
                                                 sx={{width: 30}}/>} size={"lg"}>
                        {t("googleRegistrationActionLabel")}
                    </Button>

                    <Button size={"lg"} variant={"outlined"}
                            sx={{borderRadius: 30}}
                            onClick={() => auth.authenticateWithProvider("facebook")}
                            color={"secondary"}
                            startDecorator={<Box component={'img'}
                                                 src={require("../assets/facebook_logo.webp")}
                                                 sx={{width: 30}}/>}>
                        {t("facebookRegistrationActionLabel")}
                    </Button>
                </Stack>}

            <Typography sx={{alignSelf: "center"}}>
                {t("callToLoginActionLabel")}{" "}
                <Link to={"/login"} style={{textDecoration: "none"}}>
                    <Typography
                        component="span"
                        level="title-sm"
                        sx={{color: "primary.500", fontWeight: 600}}>
                        {t("loginActionLabel")}
                    </Typography>
                </Link>
            </Typography>

        </Card>
    </Stack>
}

export const RegisterPersonalInfoStep = (props: {
    currentStep: number,
    userFormData: UserFormData,
    onFormDataChange: (key: keyof UserFormData, value: any) => void,
    fieldValidation: {
        [field in keyof UserFormData]: {
            required: boolean,
            message?: string,
            check?: boolean
        }
    }
}) => {
    const [passwordVisible, setPasswordVisible] = useState(false)
    const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false)

    const togglePasswordVisibility = () => setPasswordVisible(prevState => !prevState)
    const toggleConfirmPasswordVisibility = () => setConfirmPasswordVisible(prevState => !prevState)
    const {t} = useTranslation()

    return <AnimatePresence mode="wait">
        <motion.div
            style={{width: "100%"}}
            key={0}
            initial={{opacity: 0, x: props.currentStep === 0 ? -50 : 50}}
            animate={{opacity: 1, x: 0}}
            exit={{opacity: -100, x: 0}}
            transition={{duration: 0.4}}>
            <Box component={"form"} display={"flex"} flexDirection={"column"} gap={2} minWidth={{sm: "35rem"}}>

                <FormControl error={props.userFormData.name && !props.fieldValidation.name.check}>
                    <FormLabel>
                        {t("fullNameFieldLabel")}
                    </FormLabel>
                    <Input size={"lg"} startDecorator={<UserIcon/>}
                           value={props.userFormData.name}
                           onChange={(event) => props.onFormDataChange("name", event.target.value)}
                           placeholder={t("fullNameFieldPlaceholder")} name={"name"}/>

                    {(props.userFormData.name && !props.fieldValidation.name.check) &&
                        <FormHelperText>
                            {props.fieldValidation.name.message}
                        </FormHelperText>}
                </FormControl>

                <FormControl error={props.userFormData.email && !props.fieldValidation.email.check}>
                    <FormLabel>
                        {t("emailFieldLabel")}
                    </FormLabel>
                    <Input size={"lg"} startDecorator={<LucideMail/>}
                           value={props.userFormData.email}
                           onChange={(event) => props.onFormDataChange("email", event.target.value)}
                           placeholder={t("emailFieldPlaceholder")} type={"email"}/>

                    {(props.userFormData.email && !props.fieldValidation.email.check) &&
                        <FormHelperText>
                            {props.fieldValidation.email.message}
                        </FormHelperText>}
                </FormControl>

                <FormControl error={props.userFormData.password && !props.fieldValidation.password.check}>
                    <FormLabel>
                        {t("passwordFieldLabel")}
                    </FormLabel>
                    <Input size={"lg"} startDecorator={<LockIcon/>}
                           endDecorator={passwordVisible ?
                               <EyeOff style={{cursor: "pointer"}} onClick={togglePasswordVisibility}/> :
                               <Eye onClick={togglePasswordVisibility} style={{cursor: "pointer"}}/>}
                           value={props.userFormData.password}
                           onChange={(event) => props.onFormDataChange("password", event.target.value)}
                           placeholder={t("passwordFieldPlaceholder")}
                           type={passwordVisible ? "text" : "password"}/>

                    <FormHelperText>
                        {(props.userFormData.password && !props.fieldValidation.password.check) ? props.fieldValidation.password.message : t("passwordFieldHelperText")}
                    </FormHelperText>
                </FormControl>

                <FormControl
                    error={(props.userFormData.confirmPassword && !props.fieldValidation.confirmPassword.check)}>
                    <FormLabel>
                        {t("confirmPasswordFieldLabel")}
                    </FormLabel>
                    <Input size={"lg"} startDecorator={<LockIcon/>}
                           endDecorator={confirmPasswordVisible ?
                               <EyeOff style={{cursor: "pointer"}} onClick={toggleConfirmPasswordVisibility}/> :
                               <Eye style={{cursor: "pointer"}} onClick={toggleConfirmPasswordVisibility}/>}
                           value={props.userFormData.confirmPassword}
                           onChange={(event) => props.onFormDataChange("confirmPassword", event.target.value)}
                           placeholder={t("confirmPasswordFieldPlaceholder")}
                           type={confirmPasswordVisible ? "text" : "password"}/>

                    {(props.userFormData.confirmPassword && !props.fieldValidation.confirmPassword.check) &&
                        <FormHelperText>
                            {props.fieldValidation.confirmPassword.message}
                        </FormHelperText>}
                </FormControl>

                <Checkbox sx={{width: "fit-content", maxWidth: "100%"}} required
                          value={props.userFormData.confirmPassword}
                          onChange={(event) => props.onFormDataChange("agreedToTermsAndCondition", event.target.checked)}
                          label={t("termsAndConditionsFieldLabel")}/>
            </Box>

        </motion.div>

    </AnimatePresence>
}

export const TestInfoStep = (props: {
    userFormData: UserFormData,
    onFormDataChange: (key: keyof UserFormData, value: any) => void
}) => {
    const theme = useTheme()
    const [languageTier, setLanguageTier] = useState<LanguageTier>()
    const {t} = useTranslation()
    const examFieldScoreMapper: { [key in ExamType]?: string } = {
        tcf_ca: "clb",
        ielts: "band",
        tcf_fr: "points",
        toefl: "score",
    }

    useEffect(() => {
        if (languageTier)
            props.onFormDataChange("targetScore", languageTier[examFieldScoreMapper[props.userFormData?.preferredExam]])
    }, [languageTier])

    return <AnimatePresence mode="wait">

        <motion.div
            style={{width: "100%"}}
            key={0}
            initial={{opacity: 0, x: 50}}
            animate={{opacity: 1, x: 0}}
            exit={{opacity: 0, x: -100}}
            transition={{duration: 0.4}}>
            <Stack gap={3} alignItems={"center"}>
                <FormControl>
                    <FormLabel>
                        {t("preferredExamFieldDescription")}
                    </FormLabel>

                    <RadioGroup
                        aria-label="platform"
                        overlay
                        value={props.userFormData.preferredExam}
                        onChange={(event) => props.onFormDataChange("preferredExam", event.target.value)}
                        sx={{
                            flexDirection: 'row',
                            gap: 2,
                            [`& .${radioClasses.checked}`]: {
                                [`& .${radioClasses.action}`]: {
                                    inset: -1,
                                },
                            },
                            [`& .${radioClasses.radio}`]: {
                                display: 'contents',
                                '& > svg': {
                                    zIndex: 2,
                                    position: 'absolute',
                                    top: '10px',
                                    right: '10px',
                                    bgcolor: 'background.surface',
                                    borderRadius: '50%',
                                },
                            },
                        }}>
                        <Grid container spacing={2}>
                            {EXAM_TYPE_MAPPER.map((exam) => (
                                <Grid sm={6} xs={12}>
                                    <Card sx={{gap: 1, opacity: exam.available ? 1 : 0.5}} data-no-animation data-no-glow>

                                        {!exam.available &&
                                            <Chip color={"secondary"} size={"sm"}
                                                  sx={{alignSelf: "end", position: "absolute", right: 10}}>
                                                {t("examSoonAvailableLabel")}
                                            </Chip>}

                                        <Radio color={"success"} disabled={!exam.available} id={exam.value}
                                               value={exam.value}
                                               checkedIcon={<CheckCircle2
                                                   style={{fontSize: 30, color: theme.palette.success['400']}}/>}/>
                                        <Typography textTransform={"uppercase"} level={"title-md"}>
                                            {exam.label}
                                        </Typography>
                                        <Typography level={"body-sm"}>
                                            {exam.description}
                                        </Typography>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>

                    </RadioGroup>
                </FormControl>

                {props.userFormData?.preferredExam &&
                    <FormControl>
                        <FormLabel>
                            {t("targetScoreFieldDescription")}
                        </FormLabel>

                        <RadioGroup
                            aria-label="platform"
                            defaultValue="Website"
                            overlay
                            value={languageTier?.id}
                            onChange={event =>
                                setLanguageTier(LANGUAGE_TIERS[props.userFormData?.preferredExam].tiers.find(value => value.id === event.target.value))}
                            sx={{
                                flexDirection: 'row',
                                gap: 2,
                                [`& .${radioClasses.checked}`]: {
                                    [`& .${radioClasses.action}`]: {
                                        inset: -1,
                                    },
                                },
                                [`& .${radioClasses.radio}`]: {
                                    display: 'contents',
                                    '& > svg': {
                                        zIndex: 2,
                                        position: 'absolute',
                                        top: '10px',
                                        right: '10px',
                                        bgcolor: 'background.surface',
                                        borderRadius: '50%',
                                    },
                                },
                            }}
                        >

                            <Grid container spacing={2}>
                                {LANGUAGE_TIERS[props.userFormData?.preferredExam].tiers.map((value) => (
                                    <Grid sm={6} xs={12}>
                                        <Card sx={{gap: 1}} data-no-animation data-no-glow>
                                            <Radio color={"success"} id={value.id} value={value.id}
                                                   checkedIcon={<CheckCircle2
                                                       style={{fontSize: 30, color: theme.palette.success['400']}}/>}/>
                                            {props.userFormData.preferredExam === "tcf_fr" ?
                                                <Typography textTransform={"uppercase"} level={"title-md"}>
                                                    {t(value.label)} ({value.cefr[0]})
                                                </Typography> :
                                                <Typography textTransform={"uppercase"} level={"title-md"}>
                                                    {t(value.label)} ({value[examFieldScoreMapper[props.userFormData?.preferredExam]].min} - {value[examFieldScoreMapper[props.userFormData?.preferredExam]].max})
                                                </Typography>}
                                            <Typography level={"body-sm"}>
                                                {t(value.description)}
                                            </Typography>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>

                        </RadioGroup>

                    </FormControl>}

            </Stack>
        </motion.div>
    </AnimatePresence>
}

export default Register